import { useCallback, useEffect, useState } from "react";
import { Page, Card, IndexTable, Badge, Button, ButtonGroup, Spinner } from "@shopify/polaris";
import { useNavigate } from "react-router-dom";
import { api } from "../api";
import posthog from "../posthog";
import type { Rule } from "../types";
import EmptyState from "../components/EmptyState";

export default function Rules(): JSX.Element {
  const navigate = useNavigate();
  const [rules, setRules] = useState<Rule[]>([]);
  const [loading, setLoading] = useState(true);

  const loadRules = useCallback(() => {
    setLoading(true);
    api
      .get<{ rules: Rule[] }>("/rules")
      .then(({ rules: fetched }) => setRules(fetched))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadRules();
  }, [loadRules]);

  async function toggleEnabled(rule: Rule): Promise<void> {
    await api.put(`/rules/${rule.id}`, { enabled: !rule.enabled });
    posthog.capture("shipping_rule_enabled_changed", { enabled: !rule.enabled });
    loadRules();
  }

  async function removeRule(rule: Rule): Promise<void> {
    await api.delete(`/rules/${rule.id}`);
    posthog.capture("shipping_rule_deleted");
    loadRules();
  }

  if (loading) {
    return (
      <Page title="Rules">
        <Spinner accessibilityLabel="Loading rules" size="large" />
      </Page>
    );
  }

  return (
    <Page
      title="Rules"
      subtitle="Optional. Pin a carrier or tighten the deadline for matching orders; unmatched orders still rate-shop."
      primaryAction={{ content: "Create rule", onAction: () => navigate("/rules/new") }}
    >
      {rules.length === 0 ? (
        <EmptyState
          heading="No rules yet — that is fine"
          action={{ content: "Create rule", onAction: () => navigate("/rules/new") }}
        >
          <p>
            Without a rule, every paid order is rate-shopped across USPS, UPS, FedEx, and DHL.
            Add a rule only if you want to pin a carrier or tighten the deadline for some
            orders. A miss still buys a label.
          </p>
        </EmptyState>
      ) : (
        <Card padding="0">
          <IndexTable
            resourceName={{ singular: "rule", plural: "rules" }}
            itemCount={rules.length}
            headings={[
              { title: "Priority" },
              { title: "Name" },
              { title: "Status" },
              { title: "Carrier" },
              { title: "Actions" },
            ]}
            selectable={false}
          >
            {rules.map((rule, index) => (
              <IndexTable.Row id={String(rule.id)} key={rule.id} position={index}>
                <IndexTable.Cell>{rule.priority}</IndexTable.Cell>
                <IndexTable.Cell>{rule.name}</IndexTable.Cell>
                <IndexTable.Cell>
                  <Badge tone={rule.enabled ? "success" : undefined}>{rule.enabled ? "Enabled" : "Disabled"}</Badge>
                </IndexTable.Cell>
                <IndexTable.Cell>{rule.preferredCarrier ?? "Any (cheapest)"}</IndexTable.Cell>
                <IndexTable.Cell>
                  <ButtonGroup>
                    <Button onClick={() => navigate(`/rules/${rule.id}`)}>Edit</Button>
                    <Button onClick={() => toggleEnabled(rule)}>{rule.enabled ? "Disable" : "Enable"}</Button>
                    <Button tone="critical" onClick={() => removeRule(rule)}>
                      Delete
                    </Button>
                  </ButtonGroup>
                </IndexTable.Cell>
              </IndexTable.Row>
            ))}
          </IndexTable>
        </Card>
      )}
    </Page>
  );
}
